import { useState, useCallback, createElement } from 'react';
import { toast } from 'react-hot-toast';
import { buildTrainingPeaksPostMessage } from '@/shared/utils/tpUploadMessage';
import { buildGarminPastePayload, garminConnectCreateUrl } from '../services/garminExport';
import type { TranslationKeys } from '@/core/i18n/translations';
import type { SportType, WorkoutSegment, ZoneDefinition } from '@/types';

/**
 * Posts structured messages for TrainingPeaks upload and Garmin workout storage via the extension bridge.
 */
export function useTpGarminBridge(
  generatedXml: string,
  workoutName: string,
  language: 'en' | 'pl',
  t: TranslationKeys,
  isPro: boolean,
  setShowPricingModal: React.Dispatch<React.SetStateAction<boolean>>,
  sport: SportType,
  segments: WorkoutSegment[],
  activeZones?: ZoneDefinition[],
) {
  const [tpSending, setTpSending] = useState(false);
  const [garminSending, setGarminSending] = useState(false);

  const sendToTrainingPeaks = useCallback(() => {
    if (!isPro) {
      setShowPricingModal(true);
      toast.error(t.proFeatureRequired);
      return;
    }
    console.log('🎬 [useTpGarminBridge] sendToTrainingPeaks() START');

    if (!generatedXml) {
      toast.error(t.errorNoZwoContent);
      return;
    }

    setTpSending(true);

    let message: ReturnType<typeof buildTrainingPeaksPostMessage>;
    try {
      message = buildTrainingPeaksPostMessage(generatedXml, workoutName, language);
    } catch (e) {
      console.error('❌ [useTpGarminBridge] Base64 error:', e);
      toast.error(t.errorBase64Conversion);
      setTpSending(false);
      return;
    }

    console.log('[useTpGarminBridge] Posting UPLOAD_TO_TP, language:', language);

    try {
      window.postMessage(message, '*');
    } catch (e) {
      console.error('❌ [useTpGarminBridge] postMessage error:', e);
      toast.error(t.errorPostMessage);
      setTpSending(false);
      return;
    }

    setTimeout(() => setTpSending(false), 15000);
  }, [generatedXml, workoutName, language, t, isPro, setShowPricingModal]);

  const sendToGarminConnect = useCallback(() => {
    if (!isPro) {
      setShowPricingModal(true);
      toast.error(t.proFeatureRequired);
      return;
    }
    const payload = buildGarminPastePayload(workoutName, segments, activeZones, sport);
    if (!payload) {
      toast.error(t.errorNoZwoContent);
      return;
    }

    setGarminSending(true);

    const message = {
      type: 'STORE_GARMIN_WORKOUT' as const,
      payload,
      lang: language,
      currentLanguage: language,
      lng: language,
    };

    try {
      // Open the Garmin tab FIRST inside the user-gesture frame — most popup blockers (Chrome,
      // Edge, Brave) only honor window.open if it is the first synchronous action after a click.
      // The bridge listener picks up STORE_GARMIN_WORKOUT a few ms later, well before the React
      // editor is mounted in the new tab, so the chrome.storage payload is ready when the user
      // clicks the inject button there.
      const garminTab = window.open(garminConnectCreateUrl(sport), '_blank', 'noopener,noreferrer');
      window.postMessage(message, '*');
      if (!garminTab) {
        toast.custom(
          (ti) =>
            createElement(
              'div',
              {
                style: {
                  background: '#fff',
                  color: '#0f172a',
                  borderRadius: '14px',
                  padding: '16px 18px',
                  boxShadow: '0 12px 40px rgba(15,23,42,.18)',
                  maxWidth: '340px',
                  fontSize: '14px',
                  lineHeight: 1.45,
                },
              },
              createElement('p', { style: { margin: 0 } }, t.garminExportSuccess),
              createElement(
                'button',
                {
                  type: 'button',
                  style: {
                    marginTop: '14px',
                    width: '100%',
                    cursor: 'pointer',
                    background: '#0369a1',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '11px',
                    padding: '11px 16px',
                    fontWeight: 700,
                    fontSize: '14px',
                  },
                  onClick: () => {
                    window.open(garminConnectCreateUrl(sport), '_blank', 'noopener,noreferrer');
                    toast.dismiss(ti.id);
                  },
                },
                t.openGarminConnectBtn,
              ),
            ),
          { duration: 14000 },
        );
      } else {
        toast.success(t.garminExportSuccess, { duration: 5000 });
      }
    } catch (e) {
      console.error('❌ [useTpGarminBridge] Garmin postMessage error:', e);
      toast.error(t.errorPostMessage);
      setGarminSending(false);
      return;
    }

    setTimeout(() => setGarminSending(false), 800);
  }, [isPro, sport, workoutName, segments, activeZones, language, t, setShowPricingModal]);

  return { tpSending, garminSending, sendToTrainingPeaks, sendToGarminConnect };
}
